import {
  UserMessage,
  UserMessageType,
} from "@voxmedia/duet-context/context/cyclone-context";
import { ParselyResult } from "@voxmedia/duet-utils/parsely";

export { UserMessageType };
export type { UserMessage };

export enum ReplySort {
  MostRecs = "MOST_RECS",
  MostReplies = "MOST_REPLIES",
  NewestFirst = "NEWEST_FIRST",
  OldestFirst = "OLDEST_FIRST",
}

export enum Role {
  ADMIN = "ADMIN",
  MODERATOR = "MODERATOR",
  USER = "USER",
  UNKNOWN = "UNKNOWN",
}

export enum CommunityType {
  Unknown,
  Team,
  General,
}

export enum ContentObjectType {
  Unknown = "Unknown",
  FanPost = "FanPost",
  QuickPost = "QuickPost",
  ExternalRoot = "ExternalRoot",
  Note = "Note",
  Poll = "Poll",
}

export type CommunitySettings = {
  userIsTooNewThresholdMs?: number;
};

export type Community = {
  id: string;
  type: CommunityType;
  name?: string;
  inviteCodes?: string[];
  allowedDomains?: string[];
  settings?: CommunitySettings;
};

export enum AttachmentType {
  Twitter = "Twitter",
  Youtube = "Youtube",
  Instagram = "Instagram",
  TikTok = "TikTok",
  Bluesky = "Bluesky",
  Link = "Link",
  GIF = "GIF",
  ImageLink = "ImageLink",
  Upload = "Upload",
}

export interface Attachment {
  url: string;
  type: AttachmentType;
  id: string;
}

export type OGImage = {
  altText?: string;
  url: string;
};

export type LinkAttachment = Attachment & {
  url: string;
  type: AttachmentType;
  id: string;
  ogTitle: string | null;
  ogDescription: string | null;
  ogImage: OGImage | null;
  ogSiteName: string | null;
  publishedAt: string | null;
  favicon: string | null;
};

export type UploadAttachment = Attachment & {
  altText: string | null;
  height: number | null;
  width: number | null;
};

export type TwitterAttachment = Attachment & {
  url: string;
  type: AttachmentType;
  id: string;
  username: string | null;
  text: string | null;
  heightToWidthRatio: number | null;
  html: string | null;
  authorName: string | null;
};

export type YoutubeAttachment = Attachment & {
  url: string;
  type: AttachmentType;
  id: string;

  title: string | null;
  channelTitle: string | null;
  html: string | null;
  thumbnailUrl: string | null;
  thumbnailHeight: number | null;
  thumbnailWidth: number | null;
};

export type InstagramAttachment = Attachment & {
  url: string;
  type: AttachmentType;
  id: string;

  html: string | null;
  authorName: string | null;
  heightToWidthRatio: number | null;
  thumbnailUrl: string | null;
  thumbnailHeight: number | null;
  thumbnailWidth: number | null;
};

export type TikTokAttachment = Attachment & {
  url: string;
  type: AttachmentType;
  id: string;

  html: string | null;
  heightToWidthRatio: number | null;
  thumbnailUrl: string | null;
  thumbnailHeight: number | null;
  thumbnailWidth: number | null;
};

export type BlueskyAttachment = Attachment & {
  url: string;
  type: AttachmentType;
  id: string;

  html: string | null;
  heightToWidthRatio: number | null;
  thumbnailUrl: string | null;
  thumbnailHeight: number | null;
  thumbnailWidth: number | null;
};

export type GifAttachment = Attachment & {
  altText: string | null;
  thumbnailUrl: string | null;
};

export type ImageLinkAttachment = Attachment;

export enum ContentModerationStatus {
  Approved = "Approved",
  None = "None",
  Pending = "Pending",
  Rejected = "Rejected",
}

export interface ContentObject {
  community?: Community;
  createdAt: string;
  id: string;
  shortID: string;
  keywords?: string[];
  parentID?: string;
  type: ContentObjectType;
  deletedAt?: string | null;
  modStatus?: ContentModerationStatus;
}

export enum FanPostFeedMode {
  compact,
  full,
}

export type UserSettings = {
  fanPostFeedMode?: FanPostFeedMode;
  replySort?: ReplySort;
  unfurlMediaEmbeds?: boolean;
};

export type UserSettingsInput = {
  fanPostFeedMode?: FanPostFeedMode;
  replySort?: ReplySort;
  unfurlMediaEmbeds?: boolean;
};

export enum UserStatus {
  None = "None",
  Suspended = "Suspended",
  Banned = "Banned",
}

export type UserRestriction = {
  type: UserStatus;
  allCommunities?: boolean;
  communityID?: string;
  expiresAt?: string;
};

export type IgnoredUser = {
  id: string;
  user: Pick<User, "id" | "username">;
};

export type TrustedCommunity = {
  name: string;
  id: string;
};

export type CommunityScope = {
  communityID: string;
};

export type User = {
  id: string;
  email: string;
  username?: string;
  displayName?: string;
  displayNameOrUsername?: string;
  role: Role;
  /**
   * Present on MODERATOR viewers after a follow-up fetch.
   * `[]` means unscoped (full access). `null` means the fetch failed.
   * ADMIN and USER viewers leave this unset.
   */
  moderatorScopes?: CommunityScope[] | null;
  createdAt: string;
  avatar?: string;
  communities: string[];
  status: UserStatus;
  restrictions?: UserRestriction[];
  profileID?: string;
  profileURL?: string;
  settings?: UserSettings | null;
  isTrusted?: boolean;
  trustedInCommunities?: TrustedCommunity[];
  ignoredUsers?: IgnoredUser[];
  activeWarning?: UserMessage | null;
  activeMessages?: UserMessage[];
};

export type QuickPost = ContentObject & {
  attachments?: AnyAttachment[];
  author?: User;
  body?: string;
  community?: Community;
  createdAt: string;
  deletedAt?: string | null;
  directReplyCount?: number;
  id: string;
  shortID: string;
  isPinned?: boolean;
  isRecced?: boolean;
  isReported?: boolean;
  isSponsored?: boolean;
  keywords?: string[];
  opener?: string;
  parentID?: string;
  pinnedAt?: string;
  recs?: number;
  replies?: Array<ContentObject>;
  replyCount?: number;
  type: ContentObjectType;
  repliesAreClosed?: boolean;
  isEdited?: boolean;
  rootAncestorID?: string | null;
  rootAncestor?: AnyContentObject | null;
  parent?: AnyContentObject | null;
  seen?: boolean;
};

export type ReplyUpdate = {
  id: string;
  updatedAt: string;
  reply: QuickPost | null;
};

export type ExternalRoot = ContentObject & {
  community?: Community;
  createdAt: string;
  deletedAt?: string | null;
  title?: string | null;
  directReplyCount?: number;
  friendlyID?: string;
  id: string;
  keywords?: Array<string>;
  modStatus: ContentModerationStatus;
  parentID?: string;
  replies?: Array<ContentObject>;
  repliesAreClosed?: boolean;
  replyCount?: number;
  shortID: string;
  type: ContentObjectType;
  urls: Array<string>;
  viewerCount: number;
  /**
   * Staff-only GraphQL field (`@auth` MODERATOR/ADMIN). When true, new
   * comments on this article are held for moderator approval.
   */
  premod?: boolean | null;
};

export type FanPost = ContentObject & {
  title: string;
  attachments?: AnyAttachment[];
  author?: User;
  body?: string;
  modStatus?: ContentModerationStatus;
  community?: Community;
  createdAt: string;
  deletedAt?: string | null;
  directReplyCount?: number;
  id: string;
  shortID: string;
  isPinned?: boolean;
  isRecced?: boolean;
  isReported?: boolean;
  isSponsored?: boolean;
  /** Ad-delivery-only FanPost; excluded from organic feeds when true. */
  adOnly?: boolean | null;
  keywords?: string[];
  opener?: string;
  parentID?: string;
  pinnedAt?: string;
  recs?: number;
  replies?: Array<ContentObject>;
  replyCount?: number;
  type: ContentObjectType;
  isLegacy?: boolean;
  repliesAreClosed?: boolean;
  isEdited?: boolean;
  isReccedByStaff?: boolean;
  /**
   * Staff-only GraphQL field (`@auth` MODERATOR/ADMIN). When true, new
   * replies are held for moderator approval.
   */
  premod?: boolean | null;
  /**
   * Staff-only GraphQL field (`@auth` MODERATOR/ADMIN). Content-row trusted
   * flag used for homepage/feed eligibility. Distinct from `author.isTrusted`.
   */
  isTrusted?: boolean;
  /**
   * Staff-only GraphQL field (`@auth` MODERATOR/ADMIN). True when the
   * FanPost author has a staff role.
   */
  authorIsStaff?: boolean;
  poll?: Poll | null;
};

export type PollOption = {
  id: string;
  name: string;
};

export type PollResults = {
  choice: string;
  count: number;
};

export type PollData = {
  options: PollOption[];
  endsAt: string | null;
};

export type Poll = ContentObject & {
  pollData: PollData;
  pollResults?: PollResults[];
  myChoice?: string | null;
  isOpen: boolean;
};

export type PollOptionInput = {
  name: string;
};

export type CreatePollInput = {
  options: PollOptionInput[];
  /** Omit for open-ended ad-only polls (GAM owns flighting). */
  durationDays?: number;
};

export interface FanPostsData {
  items: FanPost[];
  hasNext?: boolean;
}

export interface FanPostsDataProps extends FanPostsData {
  endCursor?: string | null;
  startCursor?: string | null;
  mostPopularArticles?: ParselyResult;
  hasActiveArticles?: boolean;
}

export type Note = {
  id: string;
  shortID: string;
  venueID: string;
  body: string;
  author?: User;
  attachments?: AnyAttachment[];
  community?: Community;
  createdAt: string;
  deletedAt?: string | null;
  modStatus?: ContentModerationStatus;
  type: ContentObjectType;
  isPrivate?: boolean;
  isEdited?: boolean;
  isRecced?: boolean;
  isReported?: boolean;
  recs?: number;
  replyCount?: number;
  directReplyCount?: number;
  parentID?: string;
  repliesAreClosed?: boolean;
};

// A user who recced (liked) a piece of content, plus when. Clients adapt
// cyclone's row into this shape so downstream handlers stay stable.
export type ContentReactor = {
  author: User;
  reactedAt: string;
};

export type AnyContentObject = ExternalRoot | QuickPost | FanPost | Note;

export type AnyAttachment =
  | LinkAttachment
  | TwitterAttachment
  | InstagramAttachment
  | YoutubeAttachment
  | UploadAttachment
  | ImageLinkAttachment
  | GifAttachment
  | TikTokAttachment
  | BlueskyAttachment;

export enum ReportedContentReason {
  Abusive = "ABUSIVE",
  Bio = "BIO",
  Disagree = "DISAGREE",
  Offensive = "OFFENSIVE",
  Other = "OTHER",
  Spam = "SPAM",
}

export type QuickPostWithDiscussionSchema = QuickPost & {
  comment: QuickPostWithDiscussionSchema[];
  fullBody: string;
};

// A more generic type for paginated responses from Cyclone
export type PaginatedResponse<T> = {
  items: T[];
  hasNext?: boolean;
  endCursor?: string | null;
};

// Re-exported from duet-utils to avoid duplication across packages.
export type { Team, League } from "@voxmedia/duet-utils/sports";
export type CycloneBlockPostData = Record<string, FanPost>;

export type ResolveCycloneCommunityIdFn = (args: {
  url: string;
  wpSiteId?: number | null;
  domain?: string | null;
  chorusCommunityId?: number | null;
}) => string | null;

export interface SeenState {
  communityID: string;
  contentID: string;
  seen: boolean;
}
